import { gql } from '@apollo/client'
import { Caption, Chip, Col, Heading, Row } from '@farol-ds/react'
import { OfferLockCard } from '@jusbrasil-web/shared-apollo-client/__generated__/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useCallback, useEffect, useState } from 'react'

import { useRegisterLockEvent } from '../../hooks'
import { LockEventName } from '../../types'
import { FrequencyToggle } from '../frequency-toggle'
import { PlanOfferSimplifiedCard } from '../plan-offer-simplified-card'
import {
  TwoOffersSwitchFrequencyLock_OfferLockDataFragment,
  TwoOffersSwitchFrequencyLock_ViewerFragment,
} from './__generated__/two-offers-switch-frequency-lock.graphql'
import styles from './two-offers-switch-frequency-lock.module.scss'

export interface TwoOffersSwitchFrequencyLockContentProps {
  offerLockData: TwoOffersSwitchFrequencyLock_OfferLockDataFragment
  viewer: TwoOffersSwitchFrequencyLock_ViewerFragment
}

type TwoOffersCard = TwoOffersSwitchFrequencyLock_OfferLockDataFragment['body']['cards'][number]

export function TwoOffersSwitchFrequencyLockContent({
  offerLockData,
  viewer,
}: TwoOffersSwitchFrequencyLockContentProps) {
  const { registerLockEvent } = useRegisterLockEvent()
  const header = offerLockData.header
  const { title = '', toggle } = header || {}
  const { description, options = [], defaultOption } = toggle || {}
  const [monthlyFrequency] = options

  const { body } = offerLockData || {}
  const { cards } = body || {}
  const monthlyCards = cards.slice(0, 2)
  const yearlyCards = cards.slice(2)

  const [currentFrequency, setCurrentFrequency] = useState<string>(defaultOption)

  const isYearlyAndHasCreditCard = (card: TwoOffersCard) =>
    !!card.offer.installmentsText && !!card.creditCard

  const getCardsContent = useCallback(
    (frequency: string) => (frequency === monthlyFrequency ? monthlyCards : yearlyCards),
    [monthlyFrequency, monthlyCards, yearlyCards]
  )

  useEffect(() => {
    getCardsContent(currentFrequency).forEach((card) =>
      registerLockEvent({
        eventName: LockEventName.LOCK_VIEW,
        eventProperties: [card.lockEventProperties],
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendFrequencyToggleClickedEvent = useCallback((card?: TwoOffersCard) => {
    if (!card) return
    const { lockEventProperties } = card
    if (!lockEventProperties) return

    const { experiment_id, feature, current_plan, next_plan, from_component, pid } =
      lockEventProperties
    sendMetrics('Box.FrequencyToggleClicked', pid ? String(pid) : null, {
      experiment_id,
      feature,
      current_plan,
      next_plan,
      from_component,
    })
  }, [])

  function handleChangeFrequency(frequency: string): void {
    const nextCards = getCardsContent(frequency)

    sendFrequencyToggleClickedEvent(nextCards[0])
    nextCards.forEach((card) =>
      registerLockEvent({
        eventName: LockEventName.LOCK_VIEW,
        eventProperties: [card.lockEventProperties],
      })
    )
    setCurrentFrequency(frequency)
  }

  return (
    <>
      <Heading className={styles['two-offers-lock-title']} size="lg" asChild>
        <h3>{title}</h3>
      </Heading>
      <Row className={styles['two-offers-lock-toggle-row']}>
        <Col xs={12}>
          <FrequencyToggle
            options={options}
            currentFrequency={currentFrequency}
            onChangeFrequency={handleChangeFrequency}
          />
          <div className={styles['two-offers-lock-toggle-row-info']}>
            <Caption className={styles['two-offers-lock-toggle-row-info-caption']}>
              {description?.text}
            </Caption>
            <Chip size="md" kind="secondary">
              <strong>{description?.benefit}</strong>
            </Chip>
          </div>
        </Col>
      </Row>
      <div className={styles['two-offers-lock-cards']}>
        {getCardsContent(currentFrequency).map((card, index) => (
          <PlanOfferSimplifiedCard
            offerLockCard={card}
            viewer={viewer}
            key={`plan-offer-card-${index}`}
            className={styles['two-offers-lock-cards-card']}
            showCreditCardAndRemoveOneClick={isYearlyAndHasCreditCard(card as OfferLockCard)}
          />
        ))}
      </div>
    </>
  )
}

TwoOffersSwitchFrequencyLockContent.fragments = {
  offerLockData: gql`
    fragment TwoOffersSwitchFrequencyLockContent_offerLockData on OfferLockData {
      header {
        title
        toggle {
          options
          defaultOption
          description {
            text
            benefit
          }
        }
      }
      body {
        ...PlanOfferSimplifiedCard_offerLockBody
        cards {
          ...PlanOfferSimplifiedCard_offerLockCard
          lockEventProperties {
            pid
            feature
            current_plan
            next_plan
            plan_price
            modal_type
            experiment_id
            app_id
            from_component
            resource_id
            parent_resource_id
            is_upgrade
            is_one_click
          }
        }
      }
    }
    ${PlanOfferSimplifiedCard.fragments.offerLockCard}
    ${PlanOfferSimplifiedCard.fragments.offerLockBody}
  `,
  viewer: gql`
    fragment TwoOffersSwitchFrequencyLockContent_viewer on Profile {
      ...PlanOfferSimplifiedCard_viewer
    }
    ${PlanOfferSimplifiedCard.fragments.viewer}
  `,
}
